import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { InboxNotification, NotificationType, TurnstileNotificationData } from 'src/app/models/core/inbox-notification.model';
import { SelectionStateService } from '../selection-state.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationNavigationService {

  constructor(
    private router: Router,
    private selectionState: SelectionStateService
  ) {}

  navigateToNotificationTarget(notification: InboxNotification): void {
    const eventId = notification.event?.id;

    if (!eventId) {
      console.error('No event ID found for notification:', notification);
      return;
    }

    if (notification.category === 'turnstile') {
      this.navigateToTurnstileHistory(notification, eventId);
      return;
    }

    const currentRole = this.selectionState.selectedRole();
    const isReceptionist = currentRole === 'recepcionist' || currentRole === 'scanner';

    switch (notification.type_notification) {
      case 'custom_message':
      case 'new_event_enabled':
        if (isReceptionist) {
          this.router.navigate(['scanner', eventId]);
        } else {
          this.router.navigate([`/event-sales/${eventId}`]);
        }
        break;

      case 'new_product_enabled':
        if (isReceptionist) {
          this.router.navigate(['scanner', eventId]);
        } else {
          this.router.navigate([`/event-sales/${eventId}`]);
        }
        break;

      case 'new_assistant_registered':
        this.router.navigate([`/event-sales/${eventId}/all-sold-tickets`]);
        break;

      case 'ecommerce_sale':
        const ecommerceData = notification.data as any;
        const queryParams: any = {};
        
        if (ecommerceData?.ticket_id) {
          queryParams.ticketId = ecommerceData.ticket_id;
        } 
        
        this.router.navigate([`/event-sales/${eventId}/all-sold-tickets`], {
          queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined
        });
        break;

      case 'report_event':
        this.router.navigate([`/event-reports/event/${eventId}/reports`]);
        break;

      case 'turnstile_disconnected':
        this.router.navigate([`/turnstile-monitor/${eventId}/stats`]);
        break;

      default:
        console.warn('Unknown notification type:', notification.type_notification);
    }
  }

  private navigateToTurnstileHistory(notification: InboxNotification, eventId: number): void {
    if (notification.type_notification === 'turnstile_disconnected') {
      this.router.navigate([`/turnstile-monitor/${eventId}/stats`]);
      return;
    }

    const data = notification.data as TurnstileNotificationData;
    const queryParams: any = {};

    if (data.assistant_dni) {
      queryParams.search = data.assistant_dni;
    } else if (data.assistant_name) {
      queryParams.search = data.assistant_name;
    }

    const statusFilter = this.mapNotificationTypeToStatus(notification.type_notification);
    if (statusFilter) {
      queryParams.status = statusFilter;
    }

    if (data.turnstile_id) {
      queryParams.turnstileId = data.turnstile_id;
    }

    this.router.navigate([`/turnstile-monitor/${eventId}/history`], {
      queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined
    });
  }


  private mapNotificationTypeToStatus(type: NotificationType): 'not_found' | 'used_ticket' | 'out_of_time' | 'rejected_not_used_ticket' | null {
    switch (type) {
      case 'turnstile_qr_not_found':
      case 'turnstile_qr_not_recognized':
        return 'not_found';
      
      case 'turnstile_ticket_used':
        return 'used_ticket';
      
      case 'turnstile_product_finished':
        return 'out_of_time';
      
      case 'turnstile_product_not_started':
        return 'rejected_not_used_ticket';
      
      default:
        return null;
    }
  }

  getNotificationIcon(type: NotificationType): string {
    switch (type) {
      case 'new_event_enabled':
        return 'assets/icons/event-default.png';
      case 'new_product_enabled':
        return 'assets/icons/product-default.png';
      case 'new_assistant_registered':
        return 'assets/icons/assistant_default.png';
      case 'ecommerce_sale':
        return 'assets/icons/seller.png';
      case 'report_event':
        return 'assets/icons/sales-manager.png';
      case 'turnstile_disconnected':
      case 'turnstile_qr_not_found':
      case 'turnstile_qr_not_recognized':
      case 'turnstile_ticket_used':
      case 'turnstile_product_finished':
      case 'turnstile_product_not_started':
        return 'assets/icons/Default.png';
      default:
        return 'assets/icons/Default.png';
    }
  }

  getNotificationActionText(type: NotificationType): string {
    switch (type) {
      case 'new_event_enabled':
        return 'Ver evento';
      case 'new_product_enabled':
        return 'Ver producto';
      case 'new_assistant_registered':
        return 'Ver ticket';
      case 'ecommerce_sale':
        return 'Ver ticket';
      case 'report_event':
        return 'Ver reporte';
      case 'turnstile_disconnected':
        return 'Ver molinetes';
      case 'turnstile_qr_not_found':
      case 'turnstile_qr_not_recognized':
      case 'turnstile_ticket_used':
      case 'turnstile_product_finished':
      case 'turnstile_product_not_started':
        return 'Ver ticket';
      default:
        return 'Ver más';
    }
  }
}
